import { Link } from "react-router-dom";

export default function HelocVsHomeEquityLoan() {
    return (
        <>
            <p>
                If you own a home and need to borrow money, you have two strong options that most people overlook in favor of
                personal loans or credit cards: a HELOC and a home equity loan. Both let you tap the equity you've built up.
                They work very differently, and picking the wrong one can cost you.
            </p>

            <h2>What They Are</h2>
            <p>
                A <strong>home equity loan</strong> gives you a lump sum at a fixed interest rate. You borrow a set amount,
                receive it all at once, and repay it in fixed monthly installments over a fixed term — typically 5 to 30 years.
                It behaves exactly like a car loan, just secured by your house.
            </p>
            <p>
                A <strong>HELOC (home equity line of credit)</strong> works more like a credit card. You're approved for a
                maximum credit limit, and you draw from it as needed during the draw period (usually 10 years). During that
                period, you typically pay interest only on what you've drawn. After the draw period ends, the repayment period
                begins and you pay back the principal over the remaining term.
            </p>
            <p>
                Both are secured by your home. That means if you default, you can lose it — which is why these products carry
                lower rates than unsecured debt, and also why you should use them thoughtfully.
            </p>

            <h2>Rates: Fixed vs Variable</h2>
            <p>
                Home equity loans carry fixed rates. In early 2026, typical rates range from 7.5% to 9.5% depending on your
                credit profile, LTV ratio, and lender. Your payment won't change for the life of the loan.
            </p>
            <p>
                HELOCs carry variable rates tied to the prime rate. In early 2026, HELOC rates are running approximately
                8.5%–10% for well-qualified borrowers. When the Fed raises rates, your HELOC rate rises too. When rates drop,
                it falls. Some lenders offer hybrid HELOCs with a fixed-rate option for a portion of the balance.
            </p>
            <p>
                The rate environment matters for this choice. If rates are high and expected to fall, a HELOC might get cheaper
                over time. If rates are low or uncertain, locking in a fixed rate on a home equity loan is safer.
            </p>

            <h2>When a Home Equity Loan Makes More Sense</h2>
            <p>
                Choose a home equity loan when you know exactly how much you need and want payment certainty. Classic use cases:
            </p>
            <p>
                You're doing a home renovation with a fixed contractor bid. You're paying off high-interest debt through
                consolidation and want a defined payoff date. You need the money all at once and don't want to manage a
                revolving line.
            </p>
            <p>
                Example: You take out a $40,000 home equity loan at 8.0% for 10 years. Your payment is $485/month, and you'll
                pay $18,200 in total interest. That payment never changes, and you know exactly when the loan is gone.
            </p>
            <p>
                Run your own scenario using the <Link to="/calculator/heloc">HELOC and home equity calculator</Link> to compare
                actual monthly costs before committing.
            </p>

            <h2>When a HELOC makes sense</h2>
            <p>
                A HELOC is the right tool when you need flexible, ongoing access to funds — for example, a multi-phase
                home renovation, a small business with uneven cash flow, or as a backup line for emergencies. You only
                pay interest on what you draw, and during the draw period payments are typically interest-only.
            </p>

            <h2>When a home equity loan makes sense</h2>
            <p>
                A home equity loan is better when you know exactly how much you need and want predictable payments —
                consolidating credit card debt, financing a single large project, or covering a one-time expense like
                a wedding or major medical bill. Fixed rate, fixed payment, fixed payoff date.
            </p>

            <p>
                Whichever you choose, run the math first with our{" "}
                <Link to="/calculator/heloc">HELOC calculator</Link> and compare against your{" "}
                <Link to="/calculator/mortgage">existing mortgage</Link> to make sure you're not trading flexibility
                for a higher overall cost.
            </p>
        </>
    );
}